/**
 * Storage utilities for the CUSTOMER PORTAL session.
 *
 * The portal is a separate audience from the staff app with a DIFFERENT token
 * that is NOT interchangeable with the staff `auth_token`. These helpers use
 * their own keys so a portal session and a staff session never collide.
 *
 * Also tracks which top-level surface (`staff` | `portal`) the app should show,
 * since there is no router — App.js reads it to decide what to render.
 */

const PORTAL_KEYS = {
  ACCESS_TOKEN: 'portal_access_token',
  USER_DATA: 'portal_user_data',
};

const SURFACE_KEY = 'app_surface'; // 'staff' | 'portal'

/** Save the portal access token. Portal sessions always persist to localStorage. */
export const savePortalToken = (token) => {
  try {
    localStorage.setItem(PORTAL_KEYS.ACCESS_TOKEN, token);
  } catch (error) {
    console.error('Error saving portal token:', error);
  }
};

/** @returns {string|null} the portal access token, or null. */
export const getPortalToken = () => {
  try {
    return localStorage.getItem(PORTAL_KEYS.ACCESS_TOKEN);
  } catch (error) {
    console.error('Error getting portal token:', error);
    return null;
  }
};

/** Persist the logged-in customer profile (from /portal/me). */
export const savePortalUser = (user) => {
  try {
    localStorage.setItem(PORTAL_KEYS.USER_DATA, JSON.stringify(user));
  } catch (error) {
    console.error('Error saving portal user:', error);
  }
};

/** @returns {object|null} the stored customer profile, or null. */
export const getPortalUser = () => {
  try {
    const raw = localStorage.getItem(PORTAL_KEYS.USER_DATA);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.error('Error getting portal user:', error);
    return null;
  }
};

/** Remove all portal session data. */
export const clearPortalData = () => {
  try {
    localStorage.removeItem(PORTAL_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(PORTAL_KEYS.USER_DATA);
  } catch (error) {
    console.error('Error clearing portal data:', error);
  }
};

/** @returns {boolean} whether a portal token is present. */
export const hasPortalToken = () => !!getPortalToken();

/**
 * Which surface App.js should render: 'staff' (default) or 'portal'.
 * Stored in sessionStorage so it is PER-TAB — switching to the portal in one
 * tab must not yank a staff user's other tab onto the portal surface.
 */
export const getSurface = () => {
  try {
    return sessionStorage.getItem(SURFACE_KEY) === 'portal' ? 'portal' : 'staff';
  } catch (error) {
    return 'staff';
  }
};

/** Set the active surface (this tab) and notify same-tab listeners. */
export const setSurface = (surface) => {
  try {
    sessionStorage.setItem(SURFACE_KEY, surface === 'portal' ? 'portal' : 'staff');
    // sessionStorage never fires 'storage' in the same tab, so dispatch our own
    // event to re-render App.js here.
    window.dispatchEvent(new CustomEvent('app-surface-change', { detail: { surface } }));
  } catch (error) {
    console.error('Error setting surface:', error);
  }
};
